// Hero: full-bleed photo slider, H1, rule, lead, two buttons, then the trust bar
// floating over the hero's bottom edge. REFERENCE.md §5, §6 and the load
// sequence in §2.4. This is the page's only <h1>.
//
// Props (data.js):
//   hero       { title, titleAccent, subtitle, ctaText, ctaHref }, never empty
//   secondary  { kind: "phone" | "pricing" | "contact", label, href }
//   trust      [{ id, icon, text }], already filled; a line with an unknown number is gone
//
// The slider's clock is the progress dash: its 2s linear animation ends, and
// that moves the slide. So pausing the dash pauses the slider, and it resumes
// from where it stopped. Nothing here is a timer.

import { useCallback, useEffect, useRef, useState } from "react";
import CtaLink from "./CtaLink";
import { MessageCircle, Pause, Phone, Play, Tag } from "lucide-react";
import Words from "../Home/motion/Words";
import { Photo } from "./media";
import { documentImage } from "./preview";
import { clamp01, DESKTOP, onScrollFrame, useMediaQuery, useMotionAllowed } from "./motion/env";
import useAutoRail from "./motion/useAutoRail";
import "./Hero.css";

// The brand line opens on rooftop, the strongest shot (Landing.js says why).
const SLIDES = ["rooftop", "install", "stock", "money"];

// Two photos need a crop to stay within BRAND.md §7, anchored low so the top of the
// frame is what goes: rooftop has a manufacturer's emblem in its top 15%, and stock
// a hanging store banner with a retailer's logo that ends 44% of the way down.
const SLIDE_CROPS = {
    rooftop: { "--ld-focal": "50% 100%", "--ld-zoom": 1.18 },
    stock: { "--ld-focal": "50% 100%", "--ld-zoom": 1.78 },
};

// `sizes` is how wide a photo is really drawn: a wide one cropped into a tall hero
// is shown wider than the window. The first number scales that for a crop's zoom,
// stopping at 1.55 because the next file up is heavy for very little under the
// overlay. The second is the phone's portrait file: rooftop's has no emblem and
// needs no extra pixels.
const sizesFor = (zoom = 1, portraitZoom = zoom) =>
    `(max-width: 720px) and (orientation: portrait) ${Math.round(105 * portraitZoom)}vw, max(${Math.round(100 * zoom)}vw, ${Math.round(157 * zoom)}vh)`;

const SLIDE_SIZES = {
    rooftop: sizesFor(1.18, 1),
    install: sizesFor(),
    stock: sizesFor(1.55),
    money: sizesFor(),
};

const SECONDARY_ICONS = { phone: Phone, pricing: Tag, contact: MessageCircle };
const CMS_SLIDE = "cms";

function useSlider(heroRef, slideRefs, motion, count) {
    const [{ active, prev }, setSlides] = useState({ active: 0, prev: -1 });
    const [mounted, setMounted] = useState(false);
    const [paused, setPaused] = useState(false);
    const [offscreen, setOffscreen] = useState(false);
    const [due, setDue] = useState(false);

    // Only the first photo is needed for the first paint. The rest mount once
    // the page has finished loading, so they never compete with it.
    useEffect(() => {
        const show = () => setMounted(true);
        if (document.readyState === "complete") {
            const t = setTimeout(show, 400);
            return () => clearTimeout(t);
        }
        window.addEventListener("load", show, { once: true });
        return () => window.removeEventListener("load", show);
    }, []);

    // A hero nobody can see holds its place.
    useEffect(() => {
        const el = heroRef.current;
        if (!el || !("IntersectionObserver" in window)) return undefined;
        const io = new IntersectionObserver((entries) => {
            setOffscreen(!entries[entries.length - 1].isIntersecting);
        });
        io.observe(el);
        return () => io.disconnect();
    }, [heroRef]);

    // The dash has run out. The next photo fades in only once it is loaded and
    // decoded, so it can actually be shown: a photo still on the wire or undecoded
    // would flash the dark ground, or pop in halfway through the fade.
    useEffect(() => {
        if (!due || !mounted) return undefined;
        const next = (active + 1) % count;
        const img = slideRefs.current[next]?.querySelector("img");
        let cancelled = false;
        const go = () => {
            if (cancelled) return;
            setDue(false);
            setSlides({ active: next, prev: active });
        };
        (img?.decode ? img.decode() : Promise.resolve()).then(go, go);
        return () => { cancelled = true; };
    }, [due, mounted, active, slideRefs, count]);

    // The photo from the CMS arrived or failed, and the row changed length under the clock.
    useEffect(() => {
        setDue(false);
        setSlides({ active: 0, prev: -1 });
    }, [count]);

    const goTo = useCallback((i) => {
        setMounted(true);
        setDue(false);
        setSlides((s) => (s.active === i ? s : { active: i, prev: s.active }));
    }, []);

    const onDashEnd = useCallback((e) => {
        if (motion && e.animationName === "ld-hero-dash") setDue(true);
    }, [motion]);

    return {
        active,
        prev,
        mounted,
        paused,
        held: paused || offscreen,
        togglePause: () => setPaused((p) => !p),
        goTo,
        onDashEnd,
    };
}

// `away` is how far the hero has left the top of the screen, 0 → 1, unsmoothed.
// Desktop only; CSS reads it for the text's drift and fade and the photos' drift.
function useAway(heroRef) {
    const wide = useMediaQuery(DESKTOP);
    const motion = useMotionAllowed();
    useEffect(() => {
        const el = heroRef.current;
        if (!el || !wide || !motion) return undefined;
        let last = -1;
        const measure = () => {
            const r = el.getBoundingClientRect();
            const away = clamp01(-r.top / (r.height || 1));
            if (away === last) return;
            last = away;
            el.style.setProperty("--ld-away", away.toFixed(4));
        };
        measure();
        const off = onScrollFrame(measure);
        return () => {
            off();
            el.style.removeProperty("--ld-away");
        };
    }, [heroRef, wide, motion]);
}

function SliderControls({ count, slider, motion }) {
    const { active, held, paused, togglePause, goTo, onDashEnd } = slider;
    return (
        <div className={`ld-hero__slider ${held ? "is-held" : ""}`.trim()} role="group" aria-label="صور الواجهة">
            {motion ? (
                <button
                    type="button"
                    className="ld-hero__toggle"
                    onClick={togglePause}
                    aria-label={paused ? "شغّل الصور" : "وقّف الصور"}
                >
                    {paused ? <Play aria-hidden="true" /> : <Pause aria-hidden="true" />}
                </button>
            ) : null}
            <ol className="ld-hero__ticks" onAnimationEnd={onDashEnd}>
                {Array.from({ length: count }, (_, i) => (
                    <li key={i}>
                        <button
                            type="button"
                            className={`ld-hero__tick ${i === active ? "is-active" : ""}`.trim()}
                            onClick={() => goTo(i)}
                            aria-label={`الصورة ${i + 1} من ${count}`}
                            aria-current={i === active ? "true" : undefined}
                        >
                            <span className="ld-hero__fill" />
                        </button>
                    </li>
                ))}
            </ol>
        </div>
    );
}

function TrustBar({ trust }) {
    const rail = useRef(null);
    const wide = useMediaQuery(DESKTOP);
    useAutoRail(rail);
    if (!trust?.length) return null;
    return (
        <div className="ld-trust">
            <div className="ld-container ld-trust__wrap">
                {/* On phones this is a scroller, and a keyboard has to be able to move it. */}
                <ul
                    className="ld-trust__panel"
                    ref={rail}
                    data-ld="group"
                    tabIndex={wide ? undefined : 0}
                >
                    {trust.map(({ id, icon: Icon, text }) => (
                        <li className="ld-trust__cell" key={id}>
                            <span className="ld-disc ld-disc--sm ld-disc--on-day" aria-hidden="true">
                                <Icon />
                            </span>
                            <span className="ld-label">{text}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

export default function Hero({ hero, secondary, trust }) {
    const heroRef = useRef(null);
    const slideRefs = useRef([]);
    const motion = useMotionAllowed();
    // A photo from the CMS opens the row. If it cannot load, the bundled four carry on.
    const image = documentImage(hero.image);
    const [brokenImage, setBrokenImage] = useState("");
    const slides = image && image !== brokenImage ? [CMS_SLIDE, ...SLIDES] : SLIDES;
    const slider = useSlider(heroRef, slideRefs, motion, slides.length);
    useAway(heroRef);

    const SecondaryIcon = SECONDARY_ICONS[secondary.kind];

    return (
        <>
            <section
                className="ld-hero ld-section--night"
                id="hero"
                ref={heroRef}
                aria-labelledby="ld-hero-title"
            >
                <div className="ld-hero__bg" aria-hidden="true">
                    <div className="ld-hero__slides">
                        {slides.map((name, i) => (
                            <div
                                key={name}
                                ref={(el) => { slideRefs.current[i] = el; }}
                                className={`ld-hero__slide ${i === slider.active ? "is-active" : ""} ${i === slider.prev ? "is-prev" : ""}`.trim()}
                            >
                                {name === CMS_SLIDE ? (
                                    <picture className="ld-photo">
                                        <img src={image} alt="" width={1920} height={1080} decoding="async" onError={() => setBrokenImage(image)} />
                                    </picture>
                                ) : i === 0 || slider.mounted ? (
                                    <Photo
                                        name={name}
                                        eager={i === 0}
                                        sizes={SLIDE_SIZES[name]}
                                        style={SLIDE_CROPS[name]}
                                    />
                                ) : null}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="ld-decor" aria-hidden="true">
                    <div className="ld-gridlines" style={{ "--ld-grid-at": "70% 30%" }} />
                    <div className="ld-grain" />
                </div>

                <div className="ld-container ld-cols ld-hero__inner">
                    <div className="ld-hero__text">
                        <h1 className="ld-h1 ld-hero__title" id="ld-hero-title">
                            <span className="ld-hero__line">
                                <Words text={hero.title} wordClass="ld-word" />
                            </span>{" "}
                            <span className="ld-hero__line ld-hero__line--accent">
                                <Words text={hero.titleAccent} wordClass="ld-word" />
                            </span>
                        </h1>
                        <span className="ld-rule ld-hero__rule" aria-hidden="true" />
                        <p className="ld-lead ld-hero__lead">{hero.subtitle}</p>
                        <div className="ld-hero__ctas">
                            <CtaLink href={hero.ctaHref} className="ld-btn ld-btn--primary ld-btn--lg">
                                <span>{hero.ctaText}</span>
                            </CtaLink>
                            <a href={secondary.href} className="ld-btn ld-btn--secondary ld-btn--lg">
                                {SecondaryIcon ? <SecondaryIcon aria-hidden="true" /> : null}
                                <span>{secondary.label}</span>
                            </a>
                        </div>
                    </div>
                </div>

                <div className="ld-container ld-hero__controls">
                    <SliderControls count={slides.length} slider={slider} motion={motion} />
                </div>
            </section>
            <TrustBar trust={trust} />
        </>
    );
}
